import { useEffect, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { useKitchensink } from '../context'
import { theme } from '../theme'

const codeStyle: CSSProperties = {
  fontFamily: 'monospace',
  fontSize: '12px',
  color: theme.accent,
  backgroundColor: theme.accentSubtle,
  borderRadius: '4px',
  padding: '1px 5px',
}

const cardStyle: CSSProperties = {
  backgroundColor: theme.surface,
  border: `1px solid ${theme.border}`,
  borderRadius: '10px',
  padding: '20px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
}

const sectionTitleStyle: CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: theme.accent,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  margin: '0 0 4px',
}

const buttonStyle: CSSProperties = {
  backgroundColor: theme.surfaceElevated,
  color: theme.text,
  border: `1px solid ${theme.border}`,
  borderRadius: '7px',
  padding: '8px 14px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

type StepProps = { n: number; children: ReactNode }

const Step = ({ n, children }: StepProps) => (
  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
    <span
      style={{
        flexShrink: 0,
        width: '22px',
        height: '22px',
        borderRadius: '50%',
        backgroundColor: theme.accentSubtle,
        color: theme.accent,
        fontSize: '11px',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: '1px',
      }}
    >
      {n}
    </span>
    <p style={{ fontSize: '13px', color: theme.textMuted, margin: 0, lineHeight: 1.6 }}>
      {children}
    </p>
  </div>
)

// Simulates the LiftAi tag: writes cookies + dispatches the documented browser
// events. In production this is the customer-installed LiftAi snippet's job;
// in the kitchensink we drive it from buttons so timing can be controlled precisely.
const setCookie = (name: string, value: string) => {
  document.cookie = `${name}=${value}; path=/`
}

const clearCookie = (name: string) => {
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`
}

const dispatch = (name: string) => {
  document.dispatchEvent(new Event(name))
}

const simulate = (band: string, segment: 'LowIntent' | 'MidIntent' | 'HighIntent') => {
  setCookie('vs_conv_ai', band)
  setCookie('vs_intent', segment)
  dispatch('LiftAICookiesSet')
}

const readCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

export const LiftAiPage = () => {
  const { variant } = useKitchensink()
  const [scoreCookie, setScoreCookie] = useState<string | null>(null)
  const [intentCookie, setIntentCookie] = useState<string | null>(null)
  const [updateCount, setUpdateCount] = useState(0)
  const [deanonResolved, setDeanonResolved] = useState(false)

  // Mirrors how the chat embed consumes the new API: register the trigger, then
  // resolve the initial deanonymize(). After it resolves, each value change bumps
  // the counter once; repeated values do not.
  const listenForUpdates = () => {
    window.ChiliWebData?.onDeanonUpdate(() => setUpdateCount(count => count + 1))
    void window.ChiliWebData?.waitForDeanonymization(() => setDeanonResolved(true))
  }

  useEffect(() => {
    const refresh = () => {
      setScoreCookie(readCookie('vs_conv_ai'))
      setIntentCookie(readCookie('vs_intent'))
    }
    refresh()
    const interval = window.setInterval(refresh, 200)
    return () => window.clearInterval(interval)
  }, [])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '36px' }}>
      <div>
        <h1
          style={{
            fontSize: '24px',
            fontWeight: 700,
            color: theme.text,
            margin: '0 0 6px',
            letterSpacing: '-0.02em',
          }}
        >
          📈 LiftAi Intent Simulation
        </h1>
        <p style={{ fontSize: '13px', color: theme.textMuted, margin: 0 }}>
          Route: <code style={codeStyle}>{variant === 'spa' ? '/liftai' : '/liftai/'}</code>
        </p>
      </div>

      <div style={cardStyle}>
        <p style={sectionTitleStyle}>How to test</p>
        <Step n={1}>
          Fill in the tenant credentials above and click{' '}
          <strong style={{ color: theme.text }}>Initialize</strong>. The snippet attaches listeners
          for <code style={codeStyle}>LiftAICookiesSet</code>,{' '}
          <code style={codeStyle}>LiftAIScoreBandChange</code> and{' '}
          <code style={codeStyle}>LiftAISegmentChange</code> on{' '}
          <code style={codeStyle}>document</code>.
        </Step>
        <Step n={2}>
          Click one of the simulate buttons. The page writes{' '}
          <code style={codeStyle}>vs_conv_ai</code> and <code style={codeStyle}>vs_intent</code>{' '}
          cookies, then dispatches <code style={codeStyle}>LiftAICookiesSet</code>. The snippet
          reads the cookies and emits an event with{' '}
          <code style={codeStyle}>provider: 'LiftAi'</code> carrying the raw cookie values.
        </Step>
        <Step n={3}>
          Watch the network tab. A <code style={codeStyle}>POST /person-deanonymized-identity</code>{' '}
          fires with <code style={codeStyle}>source: 'LiftAi'</code> and the raw cookie payload (
          <code style={codeStyle}>vs_intent</code> and <code style={codeStyle}>vs_conv_ai</code> as
          their unmodified cookie strings). web-data normalizes them into the Person Intent Score /
          Segment fields. LiftAi is intentionally skipped on the jitsu pipeline.
        </Step>
        <Step n={4}>
          Click <strong style={{ color: theme.text }}>Change score band</strong> to simulate a
          mid-session band change within the same segment. The cookies update and the snippet
          re-emits because the data changed.
        </Step>
      </div>

      <div style={cardStyle}>
        <p style={sectionTitleStyle}>Simulate</p>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            data-test-id='simulate-low'
            onClick={() => simulate('0-4', 'LowIntent')}
            style={buttonStyle}
          >
            Simulate Low Intent (0-4)
          </button>
          <button
            data-test-id='simulate-mid'
            onClick={() => simulate('45-49', 'MidIntent')}
            style={buttonStyle}
          >
            Simulate Mid Intent (45-49)
          </button>
          <button
            data-test-id='simulate-high'
            onClick={() => simulate('75-79', 'HighIntent')}
            style={buttonStyle}
          >
            Simulate High Intent (75-79)
          </button>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            data-test-id='simulate-band-change'
            onClick={() => {
              setCookie('vs_conv_ai', '80-84')
              setCookie('vs_intent', 'HighIntent')
              dispatch('LiftAIScoreBandChange')
            }}
            style={buttonStyle}
          >
            Change score band (80-84, High)
          </button>
          <button
            data-test-id='clear-cookies'
            onClick={() => {
              clearCookie('vs_conv_ai')
              clearCookie('vs_intent')
            }}
            style={buttonStyle}
          >
            Clear cookies
          </button>
        </div>
      </div>

      <div style={cardStyle}>
        <p style={sectionTitleStyle}>onDeanonUpdate</p>
        <p style={{ fontSize: '13px', color: theme.textMuted, margin: 0, lineHeight: 1.6 }}>
          Click <strong style={{ color: theme.text }}>Listen for updates</strong> to register an{' '}
          <code style={codeStyle}>onDeanonUpdate</code> trigger and resolve the initial{' '}
          <code style={codeStyle}>deanonymize()</code>. After it resolves, every simulate that
          changes the deanon values bumps the counter once — repeated values do not.
        </p>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            data-test-id='register-deanon-updates'
            onClick={listenForUpdates}
            style={buttonStyle}
          >
            Listen for updates
          </button>
          {deanonResolved && (
            <span data-test-id='deanon-resolved' style={{ fontSize: '12px', color: theme.accent }}>
              deanonymize() resolved
            </span>
          )}
        </div>
        <pre
          style={{
            margin: 0,
            fontFamily: 'monospace',
            fontSize: '12px',
            color: theme.text,
            backgroundColor: theme.surfaceElevated,
            borderRadius: '8px',
            padding: '14px 16px',
            lineHeight: 1.6,
          }}
        >
          onDeanonUpdate fired: <span data-test-id='deanon-update-count'>{updateCount}</span>
        </pre>
      </div>

      <div style={cardStyle}>
        <p style={sectionTitleStyle}>Cookie state</p>
        <pre
          data-test-id='cookie-state'
          style={{
            margin: 0,
            fontFamily: 'monospace',
            fontSize: '12px',
            color: theme.text,
            backgroundColor: theme.surfaceElevated,
            borderRadius: '8px',
            padding: '14px 16px',
            overflowX: 'auto',
            lineHeight: 1.6,
          }}
        >{`vs_conv_ai = ${scoreCookie ?? '<unset>'}
vs_intent  = ${intentCookie ?? '<unset>'}`}</pre>
      </div>

      <div style={cardStyle}>
        <p style={sectionTitleStyle}>Payload shape (raw cookies)</p>
        <pre
          style={{
            margin: 0,
            fontFamily: 'monospace',
            fontSize: '12px',
            color: theme.text,
            backgroundColor: theme.surfaceElevated,
            borderRadius: '8px',
            padding: '14px 16px',
            overflowX: 'auto',
            lineHeight: 1.6,
          }}
        >{`{
  provider: 'LiftAi',
  data: {
    vs_intent: 'LowIntent' | 'MidIntent' | 'HighIntent',  // raw segment cookie
    vs_conv_ai: '15-19',                                  // raw score band cookie
  },
}`}</pre>
      </div>
    </div>
  )
}
